import React, { useRef, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useFetchStudents } from "@/hooks/useFetchStudents";
import {
  StudentListWrapper,
  View as StudentListView,
} from "@/components/StudentListWrapper";
import { FilterState } from "@/components/FilterSidebar/FilterState";
import {
  PaginationState,
  paginate,
  newState,
  getPaginationVars,
  PaginatedResults,
} from "@/util/pagination";
import { Student } from "@/types/Student";
import { useFilterSidebarQuery } from "@/hooks/useFilterSidebarQuery";
import { useStudentsListLink } from "@/routes/buildStudentsListLink";
import { toast } from "sonner";
import { StudentOrdering } from "@/types/StudentOrdering";
import { FilterContext } from "@/types/FilterContext";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { OrganizationPartnershipLevelEnum } from "@/__generated__/types";

// keep track of pagination for both grid and table view
type PaginationStates = Record<StudentListView, PaginationState>;

export const TABLE_PER_PAGE = 5;

const resetStates = (prev?: PaginationStates): PaginationStates =>
  Object.values(StudentListView).reduce((acc, key) => {
    const view = key as StudentListView;
    acc[view] = newState(prev?.[view].perPage || TABLE_PER_PAGE);
    return acc;
  }, {} as PaginationStates);

export const FilteredStudentsList = ({
  title,
  filterState,
  context,
  ordering,
  setOrdering,
  description,
  getDescription,
  onQueryResults,
  riseIcon,
  profileSource,
}: {
  title: string;
  filterState?: FilterState;
  context?: FilterContext;
  ordering?: StudentOrdering;
  setOrdering?: (ordering: StudentOrdering) => void;
  onQueryResults?: (results: PaginatedResults<Student>) => void;
  description?: string;
  getDescription?: (count: string) => string;
  riseIcon?: boolean;
  profileSource?: string;
}) => {
  const { t } = useTranslation();

  const { hasMinOrgPartnershipLevel } = useOrgPermissions();

  const canUseGridView =
    hasMinOrgPartnershipLevel(OrganizationPartnershipLevelEnum.Pro) ?? false;

  const lastValidTotalCount = useRef<number>(0);

  const [view, setView] = useState(StudentListView.GRID);
  const [gridSizeInit, setGridSizeInit] = useState(false);
  const [paginationState, setPaginationState] =
    useState<PaginationStates>(resetStates());

  // reset pagination when filter or ordering or context changed
  useEffect(() => {
    setPaginationState((prev) => resetStates(prev));
  }, [filterState, ordering, context]);

  const results = useFilterSidebarQuery(
    useFetchStudents({
      paginationVariables: getPaginationVars(paginationState[view]),
      ordering,
      context,
      filterState,
      skip: !gridSizeInit,
    }),
    "filteredStudentList",
  );

  useEffect(() => {
    if (onQueryResults && results) {
      onQueryResults(results);
    }
  }, [results, onQueryResults]);

  const {
    items: students,
    endCursor,
    startCursor,
    loading,
    totalCount,
    error,
  } = results;

  // save totalCount inbetween page loads
  lastValidTotalCount.current =
    totalCount || totalCount === 0 ? totalCount : lastValidTotalCount.current;

  const handlePageChange = useCallback(
    (newPage: number) => {
      // update page for current view
      setPaginationState((prev) => ({
        ...prev,
        [view]: paginate(newPage, prev[view], startCursor, endCursor),
      }));
    },
    [startCursor, endCursor, view],
  );

  const handlePerPageChange = useCallback(
    (perPage: number) => {
      setGridSizeInit(true);
      // update page size for current view
      setPaginationState((prev) => {
        if (prev[view].perPage === perPage) return prev;
        return {
          ...prev,
          [view]: {
            page: 1,
            perPage,
          },
        };
      });
    },
    [view],
  );

  const getStudentsListLink = useStudentsListLink();

  const handleShareClick = () => {
    const studentsListLink = getStudentsListLink({
      fs: filterState || undefined,
      ordering: ordering || undefined,
      context: context || undefined,
    });
    navigator.clipboard
      .writeText(studentsListLink)
      .then(() => {
        toast.success(t("studentList.toast.shareSuccess"));
      })
      .catch(() => {
        toast.error(t("studentList.toast.shareError"));
      });
  };

  const defaultDesc = t("filteredStudents.description", {
    totalCount: lastValidTotalCount.current,
  });

  const descOverride = getDescription
    ? getDescription(`${lastValidTotalCount.current}`)
    : description;

  return (
    <StudentListWrapper
      title={title}
      description={descOverride || defaultDesc}
      students={students}
      onViewChange={setView}
      enableGridView={canUseGridView}
      view={view}
      riseIcon={riseIcon}
      page={paginationState[view].page}
      perPage={paginationState[view].perPage}
      onShareClick={handleShareClick}
      onPageChange={handlePageChange}
      onPerPageChange={handlePerPageChange}
      loading={loading}
      totalCount={lastValidTotalCount.current}
      ordering={ordering || new StudentOrdering(null, null)}
      setOrdering={setOrdering}
      isError={!!error}
      hasActiveFilters={(filterState?.activeFilterCount() ?? 0) > 0}
      profileSource={profileSource}
    />
  );
};
